import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuSeparator,
    DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
    ChefHat, 
    Menu, 
    X, 
    User, 
    LogOut, 
    Heart, 
    BookOpen, 
    Plus, 
    Crown,
    Lightbulb,
    Home,
    Calendar,
    Shield,
    Sparkles,
    Settings
} from 'lucide-react';

export const Navbar = () => {
    const { user, logout, isPremium, isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Detect scroll to add shadow
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const navLinks = [
        { href: '/', label: 'Accueil', icon: Home },
        { href: '/weekly-pack', label: 'Pack Hebdo', icon: Calendar },
        { href: '/recipes', label: 'Recettes', icon: BookOpen },
        { href: '/tips', label: 'Astuces', icon: Lightbulb },
        { href: '/guide', label: 'Guide', icon: BookOpen },
        { href: '/dossiers', label: 'Dossiers', icon: Sparkles },
        { href: '/ai', label: 'IA', icon: Sparkles, premium: true },
        { href: '/subscription', label: 'Premium', icon: Crown },
    ];

    const isActive = (path) => location.pathname === path;

    // Caméléon colors for nav items
    const getNavColor = (index) => {
        const colors = [
            'hover:bg-green-500/10 hover:text-green-600',
            'hover:bg-cyan-500/10 hover:text-cyan-600',
            'hover:bg-purple-500/10 hover:text-purple-600',
            'hover:bg-orange-500/10 hover:text-orange-600',
            'hover:bg-pink-500/10 hover:text-pink-600',
            'hover:bg-yellow-500/10 hover:text-yellow-600',
        ];
        return colors[index % colors.length];
    };

    const getActiveColor = (index) => {
        const colors = [
            'bg-green-500 text-white',
            'bg-cyan-500 text-white',
            'bg-purple-500 text-white',
            'bg-orange-500 text-white',
            'bg-pink-500 text-white',
            'bg-yellow-500 text-foreground',
        ];
        return colors[index % colors.length];
    };

    const getMobileColor = (index) => {
        const colors = [
            'bg-green-50 border-green-200 text-green-700',
            'bg-cyan-50 border-cyan-200 text-cyan-700',
            'bg-purple-50 border-purple-200 text-purple-700',
            'bg-orange-50 border-orange-200 text-orange-700',
            'bg-pink-50 border-pink-200 text-pink-700',
            'bg-yellow-50 border-yellow-200 text-yellow-700',
        ];
        return colors[index % colors.length];
    };

    return (
        <>
            <nav className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md transition-shadow duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo - Caméléon Style */}
                        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                            <div className="relative">
                                <div className="p-2 bg-gradient-cameleon rounded-xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                                    <ChefHat className="w-6 h-6 text-white" />
                                </div>
                            </div>
                            <div className="flex flex-col leading-tight">
                                <span className="font-heading font-black text-lg">
                                    Recettes
                                </span>
                                <span className="font-heading font-black text-lg -mt-1 text-gradient-cameleon">
                                    Caméléon
                                </span>
                            </div>
                        </Link>

                        {/* Desktop Navigation - Pop Pills */}
                        <div className="hidden lg:flex items-center gap-1">
                            {navLinks.map((link, index) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className={`px-3 py-1.5 rounded-full font-heading font-bold text-sm transition-all border-2 ${
                                        isActive(link.href)
                                            ? `${getActiveColor(index)} border-foreground shadow-pop`
                                            : `border-transparent ${getNavColor(index)} text-foreground`
                                    }`}
                                    data-testid={`nav-${link.label.toLowerCase()}`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>

                        {/* User Menu */}
                        <div className="flex items-center gap-2">
                            {user ? (
                                <>
                                    <Button
                                        className="hidden sm:flex items-center gap-2 btn-pop bg-secondary text-white border-foreground text-sm px-3 py-1"
                                        onClick={() => navigate('/recipes/new')}
                                        data-testid="add-recipe-btn"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span className="hidden md:inline">Ajouter</span>
                                    </Button>
                                    
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button 
                                                variant="ghost" 
                                                className="flex items-center gap-2 rounded-full px-2 py-1 border-2 border-transparent hover:border-foreground transition-all"
                                                data-testid="user-menu-trigger"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-gradient-cameleon flex items-center justify-center border-2 border-foreground">
                                                    <User className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="hidden md:block font-heading font-bold text-sm">
                                                    {user.name}
                                                </span>
                                                {isPremium && (
                                                    <Crown className="w-4 h-4 text-yellow-500" />
                                                )}
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-56 rounded-2xl border-2 border-foreground shadow-pop">
                                            <div className="px-3 py-2 bg-gradient-cameleon rounded-t-xl -m-1 mb-1">
                                                <p className="text-sm font-bold text-white">{user.name}</p>
                                                <p className="text-xs text-white/80">{user.email}</p>
                                            </div>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => navigate('/my-recipes')} className="cursor-pointer font-medium" data-testid="my-recipes-link">
                                                <BookOpen className="w-4 h-4 mr-2 text-green-500" />
                                                Mes recettes
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate('/favorites')} className="cursor-pointer font-medium" data-testid="favorites-link">
                                                <Heart className="w-4 h-4 mr-2 text-pink-500" />
                                                Mes favoris
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer font-medium" data-testid="account-link">
                                                <Settings className="w-4 h-4 mr-2 text-gray-500" />
                                                Mon compte
                                            </DropdownMenuItem>
                                            {isAdmin && (
                                                <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer font-medium" data-testid="admin-link">
                                                        <Shield className="w-4 h-4 mr-2 text-purple-500" />
                                                        Administration
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem 
                                                onClick={() => { logout(); navigate('/'); }}
                                                className="text-destructive cursor-pointer font-medium"
                                                data-testid="logout-btn"
                                            >
                                                <LogOut className="w-4 h-4 mr-2" />
                                                Déconnexion
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            ) : (
                                <div className="hidden sm:flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        onClick={() => navigate('/login')}
                                        className="font-heading font-bold rounded-full text-sm"
                                        data-testid="login-btn"
                                    >
                                        Connexion
                                    </Button>
                                    <Button
                                        onClick={() => navigate('/register')}
                                        className="btn-pop bg-primary text-white text-sm"
                                        data-testid="register-btn"
                                    >
                                        S'inscrire
                                    </Button>
                                </div>
                            )}

                            {/* Mobile menu button - Colorful Pop Style */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className={`lg:hidden rounded-xl w-11 h-11 transition-all duration-300 ${
                                    mobileMenuOpen 
                                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-2 border-foreground shadow-pop rotate-90' 
                                        : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-2 border-foreground shadow-pop hover:shadow-pop-hover hover:scale-105'
                                }`}
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                data-testid="mobile-menu-btn"
                            >
                                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation - Slide down */}
                <div 
                    className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
                        mobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="px-4 py-4 bg-white border-t-2 border-gray-100 shadow-inner">
                        <div className="flex flex-col gap-2">
                            {navLinks.map((link, index) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-heading font-bold transition-all border-2 ${
                                        isActive(link.href)
                                            ? `${getActiveColor(index)} border-foreground shadow-md`
                                            : `${getMobileColor(index)} border-current/20`
                                    }`}
                                >
                                    <link.icon className="w-5 h-5" />
                                    {link.label}
                                    {link.premium && !isPremium && (
                                        <Crown className="w-4 h-4 ml-auto text-yellow-500" />
                                    )}
                                </Link>
                            ))}
                            
                            {/* Mobile auth buttons */}
                            {!user && (
                                <div className="flex gap-2 mt-4 pt-4 border-t-2 border-gray-100">
                                    <Button
                                        variant="outline"
                                        onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                                        className="flex-1 font-heading font-bold rounded-xl border-2 border-foreground"
                                    >
                                        Connexion
                                    </Button>
                                    <Button
                                        onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                                        className="flex-1 btn-pop bg-primary text-white"
                                    >
                                        S'inscrire
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
            
            {/* Spacer to prevent content from going under fixed navbar */}
            <div className="h-16" />
        </>
    );
};

export default Navbar;
